// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TruncatedText } from '@/components/truncated-text';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Shipment, ShipmentItem } from '@/shipment/api/types';
import { CorrectQuantityDialog } from '@/shipment/components/correct-quantity-dialog';
import { QuarantineShipmentChip } from '@/shipment/components/quarantine-shipment-chip';
import { ShipmentItemTypeIcon } from '@/shipment/components/shipment-item-type-icon';
import { ShipmentSerialsDialog } from '@/shipment/components/shipment-serials-dialog';
import { ShipmentTotalValue } from '@/shipment/components/shipment-total-value';
import { ShipmentValueDisplay } from '@/shipment/components/shipment-value-display';
import { canCorrectLineItemQuantity } from '@/shipment/utilities/shipment-actions';
import { outboundPlannedQuantity } from '@/shipment/utilities/shipment-planned-quantity';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import { shipmentUnconfirmedTotal } from '@/shipment/utilities/shipment-unconfirmed-total';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';

interface Props {
  shipment: Shipment;
  isInbound: boolean;
}

/**
 * Height of a small text Button, so a value in the Value column sits on the same
 * line as the serials button it belongs to. The two live in different cells, so
 * nothing else keeps them aligned.
 */
const SERIAL_ROW_HEIGHT = 30.75;

/**
 * Holds one value on the same line as its serials button.
 */
function SerialValueRow(props: { children: ReactNode }) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'flex-end',
        minHeight: `${SERIAL_ROW_HEIGHT}px`,
      }}
    >
      {props.children}
    </Box>
  );
}

/**
 * Determines if a shipment item is a bulk item (can have quantity edited).
 * Bulk items are:
 * - All non-device items (!hasAssets - these are bulk/parts)
 * - Service items (!isPhysical) without serials
 */
function isBulkItem(lineItem: ShipmentItem): boolean {
  // Non-device items (bulk/parts) are always bulk
  if (!lineItem.item.hasAssets) {
    return true;
  }
  // Device items with serials cannot have quantity edited
  if (lineItem.serials && lineItem.serials.length > 0) {
    return false;
  }
  // Service items (device without physical inventory) are bulk
  return !lineItem.item.isPhysical;
}

/**
 * Table displaying shipment line items.
 * Shows item name (linked), SKU, quantities, serials, and external reference.
 * For shipped/delivered outbound shipments with bulk items, shows a "Correct error" action.
 */
export function ShipmentDetailLineItemsTable(props: Props) {
  const { shipment, isInbound } = props;
  const items = shipment.items;

  const unconfirmedTotal = useMemo(
    () => shipmentUnconfirmedTotal(items),
    [items]
  );

  const serialsDialog = useDisclosure();
  const correctQuantityDialog = useDisclosure();
  const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
  const [selectedPendingSerials, setSelectedPendingSerials] = useState<
    string[]
  >([]);
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<ShipmentItem | null>(null);

  const showCorrectionActions = canCorrectLineItemQuantity(shipment);

  const handleOpenSerials = (
    serials: string[],
    pendingSerials: string[],
    itemName: string
  ) => {
    setSelectedSerials(serials);
    setSelectedPendingSerials(pendingSerials);
    setSelectedItemName(itemName);
    serialsDialog.open();
  };

  const handleCloseSerials = () => {
    serialsDialog.close();
    setSelectedSerials([]);
    setSelectedPendingSerials([]);
    setSelectedItemName('');
  };

  const handleCorrectError = (item: ShipmentItem) => {
    setSelectedItem(item);
    correctQuantityDialog.open();
  };

  const handleCloseCorrectQuantity = () => {
    correctQuantityDialog.close();
    setSelectedItem(null);
  };

  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No items in this shipment
        </Typography>
      </Box>
    );
  }

  return (
    <Box paddingY={2}>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Planned qty</TableCell>
              <TableCell align="right">Actual qty</TableCell>
              <TableCell>Reference ID</TableCell>
              <TableCell>Serials</TableCell>
              <TableCell align="right">Value</TableCell>
              {showCorrectionActions && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((lineItem, index) => {
              const name = lineItem.item.name ?? 'Unknown';
              const sku = lineItem.item.sku;
              // Show serials for tracked devices or bulk items whose display flag is on.
              const showSerials = shipmentLineAllowsSerials(lineItem.item);
              const serials = showSerials ? lineItem.serials : null;
              const pendingSerials = showSerials
                ? lineItem.pendingSerials
                : null;
              const hasSerials = serials && serials.length > 0;
              const hasPendingSerials =
                pendingSerials && pendingSerials.length > 0;
              const hasAnySerials = hasSerials || hasPendingSerials;
              const itemIsBulk = isBulkItem(lineItem);
              const pendingSerialCount = showSerials
                ? lineItem.pendingSerialCount
                : 0;
              const hasQuarantined = lineItem.quarantinedQuantity > 0;
              // The line's own value stands on its own: a held line whose item
              // does not display serials still has a value to report.
              const hasLineValue =
                lineItem.value !== null || lineItem.estimatedValue !== null;
              // An outbound line's quantity drops the units it is holding, so
              // add them back to describe what the 3PL said it was shipping.
              // Inbound reads its planned count from the order.
              const plannedQuantity = isInbound
                ? lineItem.quantity
                : outboundPlannedQuantity(
                    lineItem.quantity,
                    lineItem.quarantinedQuantity
                  );

              return (
                <TableRow key={`${lineItem.id}-${index}`}>
                  {/* Item column - Icon + SKU (Name) format */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ShipmentItemTypeIcon item={lineItem} />
                      <Typography variant="body2">
                        {sku}{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          ({name})
                        </Typography>
                      </Typography>
                      {pendingSerialCount > 0 && (
                        <Tooltip
                          arrow
                          title={`Pending serials: ${formatNumber(pendingSerialCount)}`}
                        >
                          <Chip
                            label={formatNumber(pendingSerialCount)}
                            size="small"
                            color="info"
                            sx={{
                              height: '20px',
                              fontSize: '0.75rem',
                            }}
                          />
                        </Tooltip>
                      )}
                      {hasQuarantined && (
                        <QuarantineShipmentChip
                          openCount={lineItem.quarantinedQuantity}
                          shipmentKey={props.shipment.key}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  {/* Planned quantity column */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {plannedQuantity !== null
                        ? formatNumber(plannedQuantity)
                        : '—'}
                    </Typography>
                  </TableCell>

                  {/* Actual quantity column */}
                  <TableCell align="right">
                    <Typography variant="body2">
                      {isInbound
                        ? lineItem.receivedQuantity !== null
                          ? formatNumber(lineItem.receivedQuantity)
                          : '—'
                        : hasSerials
                          ? formatNumber(serials.length)
                          : lineItem.quantity !== null
                            ? formatNumber(lineItem.quantity)
                            : '—'}
                    </Typography>
                  </TableCell>

                  {/* Reference ID column */}
                  <TableCell sx={{ maxWidth: 240 }}>
                    {lineItem.externalReference ? (
                      <TruncatedText
                        text={lineItem.externalReference}
                        variant="body2"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                  </TableCell>

                  {/* Serials column */}
                  <TableCell>
                    {hasAnySerials ? (
                      <Stack alignItems="flex-start">
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            handleOpenSerials(
                              serials ?? [],
                              pendingSerials ?? [],
                              name
                            )
                          }
                          sx={{ minWidth: 'auto' }}
                        >
                          {hasPendingSerials && !hasSerials
                            ? 'View pending serials'
                            : 'View confirmed serials'}
                        </Button>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    {hasAnySerials || hasQuarantined ? (
                      <Stack alignItems="flex-end">
                        {hasLineValue && (
                          <SerialValueRow>
                            <ShipmentValueDisplay
                              actualValue={lineItem.value}
                              estimatedValue={lineItem.estimatedValue}
                            />
                          </SerialValueRow>
                        )}
                        {hasQuarantined && (
                          <SerialValueRow>
                            <ShipmentValueDisplay
                              actualValue={null}
                              estimatedValue={
                                lineItem.quarantinedEstimatedValue
                              }
                            />
                          </SerialValueRow>
                        )}
                      </Stack>
                    ) : (
                      <ShipmentValueDisplay
                        actualValue={lineItem.value}
                        estimatedValue={lineItem.estimatedValue}
                      />
                    )}
                  </TableCell>

                  {/* Actions column - only for outbound shipped/delivered */}
                  {showCorrectionActions && (
                    <TableCell>
                      {itemIsBulk ? (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleCorrectError(lineItem)}
                          sx={{ minWidth: 'auto' }}
                        >
                          Correct error
                        </Button>
                      ) : (
                        <Tooltip
                          arrow
                          title="This line item moved serialized assets. To make a correction, you can directly update the locations of those assets."
                        >
                          <span>
                            <Button
                              variant="text"
                              size="small"
                              disabled
                              sx={{ minWidth: 'auto' }}
                            >
                              Correct error
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5} align="right">
                <Typography variant="subtitle2">Total</Typography>
              </TableCell>
              <TableCell align="right">
                <ShipmentTotalValue
                  actualValue={shipment.totalValue}
                  estimatedValue={shipment.estimatedTotalValue}
                  unconfirmedValue={unconfirmedTotal}
                />
              </TableCell>
              {showCorrectionActions && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <ShipmentSerialsDialog
        isOpen={serialsDialog.isOpen}
        onClose={handleCloseSerials}
        serials={selectedSerials}
        pendingSerials={selectedPendingSerials}
        itemName={selectedItemName}
      />

      {selectedItem && (
        <CorrectQuantityDialog
          isOpen={correctQuantityDialog.isOpen}
          onClose={handleCloseCorrectQuantity}
          shipment={shipment}
          item={selectedItem}
        />
      )}
    </Box>
  );
}
